import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, MessageSquare } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-black/95 border-t-2 border-purple-500/30 mt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Logo et description */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_roleplay-hub-30/artifacts/5y3hduhe_JRI%20LOGO.jpg" 
                alt="Just Rôle It" 
                className="h-12 w-12 object-contain drop-shadow-[0_0_15px_rgba(147,51,234,0.8)]"
              />
              <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 tracking-wider">
                JUST RÔLE IT
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Association de jeu de rôle sur table et en ligne. <br />
              Ici, on ne raconte pas des histoires. On les vit !
            </p>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="text-purple-400 font-bold text-lg tracking-wide">Contact</h3>
            <div className="space-y-3">
              <a href="mailto:association@justroleit.fr" className="flex items-center space-x-3 text-gray-400 hover:text-purple-400 transition-colors group">
                <Mail className="h-5 w-5 group-hover:drop-shadow-[0_0_8px_rgba(147,51,234,0.6)]" />
                <span className="text-sm">association@justroleit.fr</span>
              </a>
              <a href="tel:+33689786517" data-testid="footer-phone-link" className="flex items-center space-x-3 text-gray-400 hover:text-purple-400 transition-colors group">
                <Phone className="h-5 w-5 group-hover:drop-shadow-[0_0_8px_rgba(147,51,234,0.6)]" />
                <span className="text-sm">06 89 78 65 17</span>
              </a>
              <a 
                href="https://www.herblaysurseine.fr/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-gray-400 hover:text-purple-400 transition-colors group"
              >
                <MapPin className="h-5 w-5 group-hover:drop-shadow-[0_0_8px_rgba(147,51,234,0.6)]" />
                <span className="text-sm">Herblay sur Seine, France</span>
              </a>
              <Link to="/contact" data-testid="footer-contact-form-link" className="flex items-center space-x-3 text-gray-400 hover:text-purple-400 transition-colors group">
                <MessageSquare className="h-5 w-5 group-hover:drop-shadow-[0_0_8px_rgba(147,51,234,0.6)]" />
                <span className="text-sm">Formulaire de contact</span>
              </Link>
            </div>
          </div>

          {/* Réseaux sociaux */}
          <div className="space-y-4">
            <h3 className="text-purple-400 font-bold text-lg tracking-wide">Suivez-nous</h3>
            <div className="flex space-x-4">
              <a 
                href="https://discord.com/invite/T8zYtYcYc5" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-3 bg-purple-900/20 rounded-lg border border-purple-500/30 hover:bg-purple-800/30 hover:border-purple-400/50 transition-all hover:drop-shadow-[0_0_12px_rgba(147,51,234,0.5)] group"
              >
                <svg className="h-5 w-5 text-purple-400 group-hover:text-purple-300" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/justroleit/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram Just Rôle It"
                data-testid="footer-instagram-link"
                className="p-3 bg-purple-900/20 rounded-lg border border-purple-500/30 hover:bg-purple-800/30 hover:border-purple-400/50 transition-all hover:drop-shadow-[0_0_12px_rgba(147,51,234,0.5)] group"
              >
                <svg className="h-5 w-5 text-purple-400 group-hover:text-purple-300" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-purple-500/20 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Just Rôle It. Tous droits réservés.
          </p>
          <Link to="/mentions-legales" data-testid="footer-legal-link" className="text-gray-500 hover:text-purple-400 text-sm underline-offset-4 hover:underline transition-colors">
            Mentions légales & RGPD
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;